import type { Tasting } from '../types'
import { calculateResults, isTasterComplete, type WineResult } from './scoring'
import { calculateCorrelations } from './correlation'

function formatOrdinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0])
}

function formatScoreDistribution(result: WineResult, wineCount: number): string {
  const parts: string[] = []
  for (let score = 1; score <= wineCount; score++) {
    const tasters = result.scoreDistribution.get(score)
    if (tasters && tasters.length > 0) {
      parts.push(`${formatOrdinal(score)} ×${tasters.length}`)
    }
  }
  return parts.join(', ')
}

export function generateExportText(tasting: Tasting): string {
  const results = calculateResults(tasting)
  const correlations = calculateCorrelations(tasting.tasters)
  const wineCount = tasting.wines.length

  const lines: string[] = []

  // Header
  lines.push(`LWWT: ${tasting.theme}`)
  lines.push('')

  // Rankings
  lines.push('RANKINGS:')
  const sortedResults = [...results].sort((a, b) => a.rank - b.rank || a.index - b.index)
  for (const result of sortedResults) {
    const wineName = result.name ?? '(unassigned)'
    const priceStr = result.price !== undefined ? ` ($${result.price})` : ''
    lines.push(
      `${result.rank}. Wine ${result.letter} - ${wineName}${priceStr} - ${result.totalPoints} pts`,
    )
    const distribution = formatScoreDistribution(result, wineCount)
    if (distribution) {
      lines.push(`   ${distribution}`)
    }
  }
  lines.push('')

  // Scores by taster
  lines.push('SCORES BY TASTER:')
  for (const taster of tasting.tasters) {
    const incomplete = !isTasterComplete(taster)
    const nameStr = incomplete ? `${taster.name} (incomplete)` : taster.name
    lines.push('')
    lines.push(`${nameStr}:`)

    // Create array of {score, wineName} by letter index, resolving via wineAssignments
    const scoredWines = taster.scores
      .map((score, letterIndex) => {
        const wineIndex = tasting.wineAssignments[letterIndex]
        const wineName = wineIndex !== null ? tasting.wines[wineIndex].name : '(unassigned)'
        return { score, wineName }
      })
      .filter((item) => item.score !== null)
      .sort((a, b) => (a.score as number) - (b.score as number))

    for (const { score, wineName } of scoredWines) {
      lines.push(`#${score} - ${wineName}`)
    }

    // Show incomplete wines if any
    const incompleteWines = taster.scores
      .map((score, letterIndex) => {
        const wineIndex = tasting.wineAssignments[letterIndex]
        const wineName = wineIndex !== null ? tasting.wines[wineIndex].name : '(unassigned)'
        return { score, wineName }
      })
      .filter((item) => item.score === null)
    if (incompleteWines.length > 0) {
      for (const { wineName } of incompleteWines) {
        lines.push(`#? - ${wineName}`)
      }
    }
  }
  lines.push('')

  // Correlation
  lines.push('TASTER CORRELATION:')
  if (correlations.closestPair) {
    const rho = correlations.closestPair.correlation.toFixed(2)
    lines.push(
      `Closest: ${correlations.closestPair.tasterA} & ${correlations.closestPair.tasterB} (ρ = ${rho})`,
    )
  } else {
    lines.push('Closest: Not enough data')
  }
  if (correlations.mostDifferentPair) {
    const rho = correlations.mostDifferentPair.correlation.toFixed(2)
    lines.push(
      `Most different: ${correlations.mostDifferentPair.tasterA} & ${correlations.mostDifferentPair.tasterB} (ρ = ${rho})`,
    )
  } else {
    lines.push('Most different: Not enough data')
  }

  return lines.join('\n')
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
